import * as React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import { useRc } from "./RcContext";

const DRAWER_WIDTH = 380;

const RcPanel: React.FC = () => {
  const {
    open,
    setOpen,
    messages,
    listening,
    thinking,
    interim,
    speechError,
    speechSupported,
    pendingConfirmations,
    toggleListen,
    sendText,
    confirm,
    cancelConfirm,
    clearSpeechError,
  } = useRc();
  const [draft, setDraft] = useState("");
  const bottomRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, interim, thinking, pendingConfirmations]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    void sendText(text);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: DRAWER_WIDTH },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: listening ? "#c62828" : "#1565c0",
          color: "#fff",
          transition: "background-color 0.2s ease",
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
          {listening ? "RC · Listening…" : thinking ? "RC · Thinking…" : "RC"}
        </Typography>
        <IconButton
          size="small"
          onClick={toggleListen}
          disabled={thinking}
          sx={{
            color: "#fff",
            bgcolor: listening ? "rgba(255,255,255,0.2)" : "transparent",
          }}
          aria-label={listening ? "Stop listening" : "Start listening"}
        >
          <MicIcon color={listening ? "inherit" : "inherit"} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setOpen(false)}
          sx={{ color: "#fff" }}
          aria-label="Close RC"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 1.5 }}>
        {messages.length === 0 && !listening && !thinking && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ask RC to navigate, search, or change things — e.g. “go to emails”,
            “open RFQ 123”, “archive this email”. Press Ctrl/Cmd+Shift+Space to talk.
          </Typography>
        )}

        {speechError && (
          <Alert
            severity="warning"
            sx={{ mb: 1 }}
            onClose={clearSpeechError}
          >
            {speechError}
          </Alert>
        )}

        {!speechSupported && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Voice input isn’t available here — type to RC instead.
          </Alert>
        )}

        <Stack spacing={1.5}>
          {messages.map((m) => (
            <Box
              key={m.id}
              sx={{
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "92%",
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor:
                  m.role === "user" ? "#e3f2fd" : "background.paper",
                border: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.25 }}
              >
                {m.role === "user" ? "You" : "RC"}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {m.content}
              </Typography>
            </Box>
          ))}

          {(listening || interim) && (
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                border: 1,
                borderColor: "error.light",
                bgcolor: "#ffebee",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "error.dark", fontWeight: 700, display: "block" }}
              >
                Hearing you
              </Typography>
              <Typography variant="body2" color="error.main" fontStyle="italic">
                {interim || "Speak now — your words will appear here as you talk."}
              </Typography>
            </Box>
          )}

          {thinking && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Thinking…
              </Typography>
            </Stack>
          )}

          <div ref={bottomRef} />
        </Stack>
      </Box>

      {pendingConfirmations.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff8e1" }}>
            {pendingConfirmations.map((c) => (
              <Box key={c.confirmToken} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {c.summary}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    disabled={thinking}
                    onClick={() => void confirm(c.confirmToken)}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={thinking}
                    onClick={() => void cancelConfirm(c.confirmToken)}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            ))}
          </Box>
        </>
      )}

      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          gap: 1,
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Message RC…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={thinking}
          autoComplete="off"
        />
        <IconButton
          type="submit"
          color="primary"
          disabled={thinking || !draft.trim()}
          aria-label="Send"
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Drawer>
  );
};

export default RcPanel;
